import * as THREE from 'three';
import { useAppStore, type Entity } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { useSketchStore } from '../state/useSketchStore';
import { BIMElement, WallElement } from '../core/types';
import type { SketchVec3 } from '../core/sketch/types';
import { solveWallRenderGeometry } from './WallGeometryUtils';
import { refreshKernelTessellation } from '../core/utils/dataTransform';
import { transformSolidCorners, translationMatrix } from '../core/sketch/transform';

/**
 * Junction-point (shared vertex) move — SketchUp-style corner editing.
 *
 * A "junction" is a world point where the defining points of one or more
 * scene objects coincide: wall runs meeting end to end, a column standing on
 * a wall corner, a CAD line endpoint touching a sketch edge, ... Grabbing
 * the junction (Move tool with an empty selection, or a wall endpoint grip
 * drag) translates EVERY welded defining point by the same delta:
 *
 * - start/end runs (walls, beams, braces, trusses) stretch along their own
 *   alignment — walls additionally get a full render-geometry re-solve so
 *   the miters at both joints stay attached;
 * - anchor-driven elements (columns, generic models, static families) ride
 *   rigidly through their location/position key;
 * - CAD entity defining points (line ends, polyline vertices, ...) move
 *   individually through the data bag;
 * - sketch solids stretch through the corner weld pool, loose edges stretch
 *   the welded endpoint only.
 *
 * Each owner commits through its own store + undo chain (the edit-router
 * contract): BIM = one updateElements batch, CAD = one cmd.modify history
 * entry, sketch = one HistoryAction per store action.
 */

/** Coincidence tolerance (meters) for defining points welded to ONE junction
 *  — twice the wall join tolerance so solver-welded ends always match. */
export const JUNCTION_WELD_TOL = 0.02;

/** geometryData keys that anchor a rigid element (the whole object rides). */
type AnchorKey = 'position' | 'center' | 'location';
/** geometryData keys of a stretchable run (only the welded end moves). */
type RunKey = 'start' | 'end';
type BimPointKey = RunKey | AnchorKey;

/** Point-cloud keys a rigid-anchor element must NOT also carry — an anchor
 *  move would orphan those shapes (floors, ducts, roofs, ...). */
const ANCHOR_SHAPE_KEYS = ['path', 'boundary', 'footprint', 'vertices', 'controlPoints', 'points'] as const;

export type JunctionSource = 'bim' | 'cad' | 'sketchSolid' | 'sketchEdge';

export interface JunctionParticipant {
    id: string;
    source: JunctionSource;
    /** Nearest matched defining point (junction anchor clustering only). */
    point: THREE.Vector3;
    /** BIM: coincident geometryData keys (run keys stretch, anchors rigid). */
    bimKeys?: BimPointKey[];
    /** Sketch loose edge: welded endpoint ids. */
    edgeEnds?: Array<'a' | 'b'>;
}

export interface SceneJunction {
    point: THREE.Vector3;
    participants: JunctionParticipant[];
}

const vecOf = (v: ArrayLike<number>): THREE.Vector3 =>
    new THREE.Vector3(v[0], v[1], (v[2] as number) ?? 0);

/**
 * Grab tolerance for a junction pick, scaled with the camera distance (1.2%
 * of it) so the grab feel stays constant across zoom levels — the same
 * formula the wall shape handles use for their snap tolerance.
 */
export function junctionGrabTolerance(camera: THREE.Camera, reference: THREE.Vector3): number {
    return Math.min(0.6, Math.max(0.03, camera.position.distanceTo(reference) * 0.012));
}

/**
 * Does a defining point `p` sit on the junction being grabbed at `origin`?
 * Normal 3D proximity, PLUS a ground-level allowance: a z≈0 defining point
 * matches at any click height, so the vertical corner edge of a wall (or a
 * ground CAD corner) is grabbable along its whole length — the cursor ray
 * lands mid-edge, the junction lives at the ground.
 */
function matchesOrigin(p: THREE.Vector3, origin: THREE.Vector3, grabTol: number): boolean {
    if (Math.hypot(p.x - origin.x, p.y - origin.y) >= grabTol) return false;
    return Math.abs(p.z - origin.z) < grabTol || Math.abs(p.z) < JUNCTION_WELD_TOL;
}

// ------------------------------------------------------------------ BIM scan

function bimParticipants(origin: THREE.Vector3, grabTol: number, excludeId: string | undefined, out: JunctionParticipant[]): void {
    useBIMStore.getState().project.elements.forEach((el) => {
        if (el.id === excludeId) return;
        if (el.parameters?.['Pinned']) return;
        const gd = el.geometryData as Record<string, unknown> | undefined;
        if (!gd) return;

        const keys: BimPointKey[] = [];
        let nearest: THREE.Vector3 | null = null;
        const track = (key: BimPointKey, v: ArrayLike<number>): void => {
            const p = vecOf(v);
            keys.push(key);
            if (!nearest || p.distanceTo(origin) < nearest.distanceTo(origin)) nearest = p;
        };

        // Stretchable run ends first — a coincident end wins over any anchor.
        (['start', 'end'] as RunKey[]).forEach((key) => {
            const v = gd[key];
            if (Array.isArray(v) && typeof v[0] === 'number' && matchesOrigin(vecOf(v), origin, grabTol)) {
                track(key, v as number[]);
            }
        });
        if (keys.length > 0) {
            out.push({ id: el.id, source: 'bim', point: nearest!, bimKeys: keys });
            return;
        }

        // Rigid anchors: only when the element is fully anchor-driven — no
        // run pair and no point cloud an anchor move would orphan.
        const hasRunOrCloud = Array.isArray(gd.start) || Array.isArray(gd.end) ||
            ANCHOR_SHAPE_KEYS.some((k) => Array.isArray(gd[k]));
        if (hasRunOrCloud) return;
        (['position', 'center', 'location'] as AnchorKey[]).forEach((key) => {
            const v = gd[key];
            if (Array.isArray(v) && typeof v[0] === 'number' && matchesOrigin(vecOf(v), origin, grabTol)) {
                track(key, v as number[]);
            }
        });
        if (keys.length > 0) out.push({ id: el.id, source: 'bim', point: nearest!, bimKeys: keys });
    });
}

// ------------------------------------------------------------------ CAD scan

/**
 * Data-bag walker over the SAME key set `transformDataBag` moves — the
 * junction move never touches points the rigid transform wouldn't, so the
 * two stay consistent about what defines an entity's shape.
 */
function walkDataBag(data: Record<string, any>, fn: (p: number[]) => void): void {
    (['start', 'end', 'position', 'center'] as const).forEach((k) => {
        const v = data[k];
        if (Array.isArray(v) && typeof v[0] === 'number') fn(v);
    });
    (['vertices', 'controlPoints'] as const).forEach((k) => {
        const v = data[k];
        if (!Array.isArray(v)) return;
        (v as unknown[]).forEach((p) => {
            if (Array.isArray(p) && typeof p[0] === 'number') fn(p as number[]);
        });
    });
    const curve = data.curve;
    if (curve && Array.isArray(curve.points)) {
        (curve.points as unknown[]).forEach((p) => {
            if (Array.isArray(p) && typeof p[0] === 'number') fn(p as number[]);
        });
    }
    const surface = data.surface;
    if (surface && Array.isArray(surface.cv)) {
        (surface.cv as unknown[]).forEach((row) => {
            if (!Array.isArray(row)) return;
            (row as unknown[]).forEach((p) => {
                if (Array.isArray(p) && typeof p[0] === 'number') fn(p as number[]);
            });
        });
    }
    const cage = data.cage;
    if (cage && Array.isArray(cage.vertices)) {
        (cage.vertices as unknown[]).forEach((p) => {
            if (Array.isArray(p) && typeof p[0] === 'number') fn(p as number[]);
        });
    }
}

/** Translate every data-bag point within `tol` of `origin` by `delta`
 *  (in place). Returns true when at least one point moved. */
function moveDataBagPointsNear(
    data: Record<string, any>,
    origin: THREE.Vector3,
    delta: THREE.Vector3,
    tol: number
): boolean {
    let touched = false;
    walkDataBag(data, (p) => {
        if (vecOf(p).distanceTo(origin) >= tol) return;
        p[0] += delta.x;
        p[1] += delta.y;
        p[2] = (p[2] ?? 0) + delta.z;
        touched = true;
    });
    return touched;
}

function cadParticipants(origin: THREE.Vector3, grabTol: number, out: JunctionParticipant[]): void {
    const app = useAppStore.getState();
    const frozen = new Set(Object.values(app.layers).filter((l) => l.frozen).map((l) => l.name));
    Object.values(app.entities).forEach((e: Entity) => {
        if (!e.visible || e.locked || frozen.has(e.layer)) return;
        if (!e.data) return;
        let nearest: THREE.Vector3 | null = null;
        walkDataBag(e.data, (p) => {
            const v = vecOf(p);
            if (!matchesOrigin(v, origin, grabTol)) return;
            if (!nearest || v.distanceTo(origin) < nearest.distanceTo(origin)) nearest = v;
        });
        if (nearest) out.push({ id: e.id, source: 'cad', point: nearest });
    });
}

// --------------------------------------------------------------- sketch scan

function sketchParticipants(origin: THREE.Vector3, grabTol: number, out: JunctionParticipant[]): void {
    const sketch = useSketchStore.getState();

    Object.values(sketch.solids).forEach((solid) => {
        if (solid.locked || solid.visible === false) return;
        let nearest: THREE.Vector3 | null = null;
        const collect = (loop: SketchVec3[]): void => {
            loop.forEach((p) => {
                const v = vecOf(p);
                if (!matchesOrigin(v, origin, grabTol)) return;
                if (!nearest || v.distanceTo(origin) < nearest.distanceTo(origin)) nearest = v;
            });
        };
        solid.faces.forEach((f) => {
            collect(f.outer);
            f.holes.forEach(collect);
        });
        if (nearest) out.push({ id: solid.id, source: 'sketchSolid', point: nearest });
    });

    Object.values(sketch.edges).forEach((edge) => {
        const ends: Array<'a' | 'b'> = [];
        let nearest: THREE.Vector3 | null = null;
        (['a', 'b'] as const).forEach((end) => {
            const v = vecOf(edge[end]);
            if (!matchesOrigin(v, origin, grabTol)) return;
            ends.push(end);
            if (!nearest || v.distanceTo(origin) < nearest.distanceTo(origin)) nearest = v;
        });
        if (ends.length > 0 && nearest) out.push({ id: edge.id, source: 'sketchEdge', point: nearest, edgeEnds: ends });
    });
}

// ----------------------------------------------------------------- junctions

/** Every object with a defining point within `grabTol` of `origin`. */
export function collectJunctionParticipants(
    origin: THREE.Vector3,
    grabTol: number,
    excludeId?: string
): JunctionParticipant[] {
    const out: JunctionParticipant[] = [];
    bimParticipants(origin, grabTol, excludeId, out);
    cadParticipants(origin, grabTol, out);
    sketchParticipants(origin, grabTol, out);
    return out;
}

/**
 * Resolve a click into a grabbable junction: the nearest matched defining
 * point becomes the anchor, and only points welded to the anchor (within
 * JUNCTION_WELD_TOL) form the participant set — two nearby-but-distinct
 * corners never merge into one grab. Returns null when nothing matches.
 */
export function collectSceneJunction(
    origin: THREE.Vector3,
    grabTol: number,
    excludeId?: string
): SceneJunction | null {
    const all = collectJunctionParticipants(origin, grabTol, excludeId);
    if (all.length === 0) return null;
    let anchor = all[0];
    let bestDist = anchor.point.distanceTo(origin);
    for (const p of all) {
        const d = p.point.distanceTo(origin);
        if (d < bestDist) {
            bestDist = d;
            anchor = p;
        }
    }
    const participants = all.filter((p) => p.point.distanceTo(anchor.point) < JUNCTION_WELD_TOL);
    return { point: anchor.point.clone(), participants };
}

// -------------------------------------------------------------------- commit

/**
 * Stretch every object welded to `point` by `delta` (the junction move
 * commit). Runs (walls/beams/braces/trusses) stretch the welded end along
 * their own alignment; walls re-solve their render geometry against the
 * POST-move world so miters between two stretched walls stay attached.
 * Anchor-driven BIM elements, CAD data-bag points and sketch corners/edge
 * ends move individually. `opts.excludeId` skips one BIM element (the wall
 * whose endpoint grip is being dragged commits its own geometry).
 *
 * Returns how many objects were actually updated.
 */
export function applyJunctionStretch(
    point: THREE.Vector3,
    delta: THREE.Vector3,
    label = 'Move Junction',
    opts: { excludeId?: string } = {}
): number {
    if (delta.lengthSq() < 1e-12) return 0;
    const participants = collectJunctionParticipants(point, JUNCTION_WELD_TOL, opts.excludeId);
    if (participants.length === 0) return 0;

    let moved = 0;

    // ---- BIM: runs stretch (walls + join re-solve), anchors ride rigidly.
    const bimStore = useBIMStore.getState();
    const elements = bimStore.project.elements;
    const batch: Array<{ id: string; updates: Partial<BIMElement> }> = [];
    const wallMoves = new Map<string, { start: [number, number, number]; end: [number, number, number] }>();
    const wallById = new Map<string, WallElement>();

    for (const p of participants) {
        if (p.source !== 'bim' || !p.bimKeys) continue;
        const el = elements.get(p.id);
        if (!el) continue;
        const gd = structuredClone(el.geometryData) as Record<string, unknown>;
        // Walls are plan elements ([x, y, 0] location lines): an elevated
        // junction (a sketch corner above the wall corner) must never lift
        // them out of the ground plane.
        const dz = el.category === 'Walls' ? 0 : delta.z;
        for (const key of p.bimKeys) {
            const v = gd[key];
            if (!Array.isArray(v)) continue;
            v[0] = (v[0] as number) + delta.x;
            v[1] = (v[1] as number) + delta.y;
            v[2] = ((v[2] as number) ?? 0) + dz;
        }
        if (el.category === 'Walls') {
            wallById.set(el.id, el as WallElement);
            wallMoves.set(el.id, {
                start: gd.start as [number, number, number],
                end: gd.end as [number, number, number],
            });
        } else {
            batch.push({ id: el.id, updates: { geometryData: gd } as Partial<BIMElement> });
        }
        moved++;
    }

    if (wallMoves.size > 0) {
        // Post-move world: every stretched wall's final endpoints, so a
        // miter between two stretched walls solves against BOTH new ends.
        const postMoveWalls: WallElement[] = [];
        elements.forEach((el) => {
            if (el.category !== 'Walls') return;
            const w = el as WallElement;
            const move = wallMoves.get(w.id);
            postMoveWalls.push(move
                ? { ...w, geometryData: { ...w.geometryData, start: move.start, end: move.end } }
                : w);
        });
        wallMoves.forEach((geometry, id) => {
            const wall = wallById.get(id)!;
            batch.push({
                id,
                updates: { geometryData: solveWallRenderGeometry(wall, geometry, postMoveWalls) } as Partial<BIMElement>,
            });
        });
    }
    if (batch.length > 0) bimStore.updateElements(batch);

    // ---- CAD: per-entity data-bag points, one undoable modify entry.
    const app = useAppStore.getState();
    const frozenLayers = new Set(Object.values(app.layers).filter((l) => l.frozen).map((l) => l.name));
    const cadBefore = new Map<string, Record<string, any>>();
    const cadAfter = new Map<string, Record<string, any>>();
    for (const p of participants) {
        if (p.source !== 'cad') continue;
        const e = app.entities[p.id];
        if (!e || !e.visible || e.locked || frozenLayers.has(e.layer) || !e.data) continue;
        const snapshot = structuredClone(e.data);
        const next = structuredClone(e.data);
        if (!moveDataBagPointsNear(next, point, delta, JUNCTION_WELD_TOL)) continue;
        refreshKernelTessellation(next);
        cadBefore.set(p.id, snapshot);
        cadAfter.set(p.id, next);
        app.updateEntity(p.id, { data: next });
        moved++;
    }
    if (cadBefore.size > 0) {
        app.pushHistory({
            type: 'cmd.modify',
            timestamp: Date.now(),
            data: { count: cadBefore.size, label },
            undo: () => {
                const a = useAppStore.getState();
                cadBefore.forEach((d, id) => a.updateEntity(id, { data: d }));
            },
            redo: () => {
                const a = useAppStore.getState();
                cadAfter.forEach((d, id) => a.updateEntity(id, { data: d }));
            },
        });
    }

    // ---- Sketch: solid corners through the weld pool, loose edge ends.
    const sketchStore = useSketchStore.getState();

    for (const p of participants) {
        if (p.source !== 'sketchSolid') continue;
        const solid = sketchStore.solids[p.id];
        if (!solid || solid.locked || solid.visible === false) continue;
        const corners: SketchVec3[] = [];
        const collectCorners = (loop: SketchVec3[]): void => {
            loop.forEach((pt) => {
                if (vecOf(pt).distanceTo(point) < JUNCTION_WELD_TOL) corners.push(pt);
            });
        };
        solid.faces.forEach((f) => {
            collectCorners(f.outer);
            f.holes.forEach(collectCorners);
        });
        if (corners.length === 0) continue;
        sketchStore.updateSolid(p.id, transformSolidCorners(solid, corners, translationMatrix(delta)), label);
        moved++;
    }

    const stretches: Array<{ id: string; ends: Array<'a' | 'b'> }> = [];
    for (const p of participants) {
        if (p.source !== 'sketchEdge' || !p.edgeEnds) continue;
        const edge = sketchStore.edges[p.id];
        if (!edge) continue;
        // Re-verify against live state: the weld must hold at commit time.
        const ends = p.edgeEnds.filter((end) => vecOf(edge[end]).distanceTo(point) < JUNCTION_WELD_TOL);
        if (ends.length > 0) stretches.push({ id: p.id, ends });
    }
    if (stretches.length > 0) {
        sketchStore.stretchLooseEdges(stretches, delta, label);
        moved += stretches.length;
    }

    return moved;
}
